import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { storage } from '../../utils/storage';
import {
  normalizeError,
  logError,
  isErrorResponse,
  ErrorCode,
} from '../../utils/errorHandler';
import {
  withUserInfoRetry,
  withResendVerificationRetry,
} from '../../utils/authRetry';

const extractUserInfo = (signInResponse, auth0User) => {
  const backendUserData =
    signInResponse?.actor_user_data ||
    signInResponse?.target_user_data ||
    signInResponse?.user ||
    signInResponse?.user_auth_info;

  if (backendUserData) {
    const email =
      backendUserData.email_address ||
      backendUserData.email ||
      auth0User?.email ||
      '';
    const emailPrefix = email.split('@')[0] || 'User';

    return {
      email,
      email_address: email,
      user_id: backendUserData.user_id,
      tenant_id: backendUserData.tenant_id || signInResponse?.tenant_id,
      name: auth0User?.name || emailPrefix,
      nickname: auth0User?.nickname || emailPrefix,
      created_at: backendUserData.created_at,
      is_deleted: backendUserData.is_deleted,
      perms: backendUserData.perms || [],
      version: backendUserData.version,
    };
  }

  if (auth0User) {
    return {
      ...auth0User,
      email_address: auth0User.email || auth0User.email_address,
    };
  }
  return {};
};

const getInitialAuthState = () => {
  try {
    const savedAuth = storage.getAuth();
    if (savedAuth && savedAuth.user) {
      return {
        user: savedAuth.user,
        sessionId: null,
        isAuthenticated: true,
        signinResponse: savedAuth.signinResponse || null,
        isLoading: true, // Keep loading true until verification checks complete
        backendSignInError: null,
        showEmailVerification: null,
        showPersonnelNotFound: null,
        showAcknowledgement: null,
        isSigningOut: false,
        pendingAccessToken: null,
        auth0User: null,
        auth0Error: null,
        whoamiData: null,
        _refs: {
          backendSignInAttempted: false,
          isProcessing: false,
          evalTokenCalled: false,
          isSigningOut: false,
        },
      };
    }
  } catch (error) {
    // If storage read fails (e.g., corrupted data, quota exceeded), 
    // clear the storage and return default state
    try {
      storage.removeAuth();
    } catch (clearError) {
      // If clearing also fails, continue with default state
    }
  }
  return {
    user: null,
    sessionId: null,
    isAuthenticated: false,
    signinResponse: null,
    isLoading: true,
    backendSignInError: null,
    showEmailVerification: false,
    showPersonnelNotFound: false,
    showAcknowledgement: false,
    isSigningOut: false,
    pendingAccessToken: null,
    auth0User: null,
    auth0Error: null,
    whoamiData: null,
    _refs: {
      backendSignInAttempted: false,
      isProcessing: false,
      evalTokenCalled: false,
      isSigningOut: false,
    },
  };
};

const initialState = getInitialAuthState();

export const initializeAuth = createAsyncThunk(
  'auth/initialize',
  async () => {
    const savedAuth = storage.getAuth();
    return savedAuth;
  }
);

export const checkEmailVerification = createAsyncThunk(
  'auth/checkEmailVerification',
  async ({ accessToken, evalToken }, { rejectWithValue }) => {
    try {
      const evalResponse = await evalToken(
        {},
        {},
        {
          Authorization: `Bearer ${accessToken}`,
        }
      );
      if (isErrorResponse(evalResponse)) {
        const normalizedError = normalizeError(evalResponse);
        logError(evalResponse, 'checkEmailVerification');
        if (normalizedError.isPersonnelError) {
          return rejectWithValue(normalizedError);
        }
        return true;
      }
      if (evalResponse && !evalResponse.is_error) {
        return evalResponse.is_email_verified === true;
      }
      return true;
    } catch (error) {
      const normalizedError = normalizeError(error);
      logError(error, 'checkEmailVerification');
      if (normalizedError.isPersonnelError) {
        return rejectWithValue(normalizedError);
      }
      return true;
    }
  }
);

export const signInToBackend = createAsyncThunk(
  'auth/signInToBackend',
  async ({ accessToken, signIn, auth0User }, { rejectWithValue }) => {
    try {
      const signInResponse = await signIn(
        {},
        {},
        {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json', // Explicitly setting content type to json
        }
      );
      if (isErrorResponse(signInResponse)) {
        const normalizedError = normalizeError(signInResponse);
        logError(signInResponse, 'signInToBackend');
        return rejectWithValue(normalizedError);
      }
      if (signInResponse && signInResponse.session_id) {
        const userInfo = extractUserInfo(signInResponse, auth0User);
        return {
          signInResponse,
          userInfo,
          sessionId: signInResponse.session_id,
          accessToken,
          auth0User,
        };
      }
      const error = {
        message: 'No valid response received from sign in. Missing session information.',
        is_error: true,
      };
      logError(error, 'signInToBackend - Invalid Response');
      return rejectWithValue(normalizeError(error));
    } catch (error) {
      return rejectWithValue({
        message: error.message || 'Failed to sign in to backend',
      });
    }
  }
);

export const resendVerificationEmail = createAsyncThunk(
  'auth/resendVerificationEmail',
  async ({ pendingAccessToken, resendVerificationEmail: resend }, { rejectWithValue }) => {
    try {
      await withResendVerificationRetry(async () => {
        const result = await resend(
          {},
          {},
          {
            Authorization: `Bearer ${pendingAccessToken}`,
          }
        );
        if (result && !isErrorResponse(result)) {
          return result;
        }
        const normalizedError = normalizeError(result);
        throw normalizedError;
      });
      return { success: true, message: 'Verification email sent successfully!' };
    } catch (error) {
      const normalizedError = normalizeError(error);
      logError(error, 'resendVerificationEmail');
      let errorMsg = normalizedError.message;
      if (normalizedError.code === ErrorCode.USER_NOT_FOUND) {
        errorMsg = 'User account not found. Please sign up first or contact support.';
      }
      return rejectWithValue({
        success: false,
        message: errorMsg,
        code: normalizedError.code,
      });
    }
  }
);

export const fetchWhoami = createAsyncThunk(
  'auth/fetchWhoami',
  async ({ whoami }, { rejectWithValue }) => {
    try {
      const response = await withUserInfoRetry(async () => {
        const result = await whoami({}, {}, {});
        if (isErrorResponse(result)) {
          const normalizedError = normalizeError(result);
          throw normalizedError;
        }
        return result;
      });
      return response;
    } catch (error) {
      const normalizedError = normalizeError(error);
      logError(error, 'fetchWhoami');
      return rejectWithValue(normalizedError);
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    setAuth0User: (state, action) => {
      state.auth0User = action.payload;
    },
    setAuth0Error: (state, action) => {
      if (action.payload === null || action.payload === undefined) {
        state.auth0Error = null;
      } else if (typeof action.payload === 'string') {
        state.auth0Error = action.payload;
      } else if (action.payload instanceof Error) {
        state.auth0Error = {
          message: action.payload.message || 'Unknown error',
          error: action.payload.error || action.payload.name || 'Error',
        };
      } else if (typeof action.payload === 'object') {
        state.auth0Error = {
          message: action.payload.message || action.payload.error_description || 'Unknown error',
          error: action.payload.error || action.payload.name || 'Error',
        };
      } else {
        state.auth0Error = String(action.payload);
      }
    },
    setBackendSignInError: (state, action) => {
      state.backendSignInError = action.payload;
    },
    setShowEmailVerification: (state, action) => {
      state.showEmailVerification = action.payload;
    },
    setShowPersonnelNotFound: (state, action) => {
      state.showPersonnelNotFound = action.payload;
    },
    setShowAcknowledgement: (state, action) => {
      state.showAcknowledgement = action.payload;
    },
    setIsSigningOut: (state, action) => {
      state.isSigningOut = action.payload;
    },
    setPendingAccessToken: (state, action) => {
      state.pendingAccessToken = action.payload;
    },
    setAuthData: (state, action) => {
      const { user, sessionId, signinResponse } = action.payload;
      state.user = user;
      state.sessionId = sessionId;
      state.signinResponse = signinResponse;
      state.isAuthenticated = true;
    },
    setUser: (state, action) => {
      state.user = action.payload;
    },
    setSessionId: (state, action) => {
      state.sessionId = action.payload;
    },
    setSigninResponse: (state, action) => {
      state.signinResponse = action.payload;
    },
    setIsAuthenticated: (state, action) => {
      state.isAuthenticated = action.payload;
    },
    clearAuth: (state) => {
      state.user = null;
      state.sessionId = null;
      state.isAuthenticated = false;
      state.signinResponse = null;
      state.backendSignInError = null;
      state.pendingAccessToken = null;
      state.showPersonnelNotFound = false;
      state.showEmailVerification = false;
      state.showAcknowledgement = false;
    },
    clearSigningOutState: (state) => {
      state.isSigningOut = false;
      state.showEmailVerification = false;
    },
    setRef: (state, action) => {
      const { key, value } = action.payload;
      if (state._refs) {
        state._refs[key] = value;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(initializeAuth.fulfilled, (state, action) => {
        if (action.payload) {
          const { user, signinResponse } = action.payload;
          state.user = user;
          state.sessionId = null;
          state.signinResponse = signinResponse;
          state.isAuthenticated = true;
          // Keep isLoading true for cached auth until verification checks complete
          // isLoading will be set to false by AuthManager after all checks
        } else {
          // No cached auth, safe to set loading to false
          state.isLoading = false;
        }
      })
      .addCase(signInToBackend.fulfilled, (state, action) => {
        const { userInfo, sessionId, signInResponse } = action.payload;
        state.user = userInfo;
        state.sessionId = sessionId;
        state.isAuthenticated = true;
        state.signinResponse = signInResponse;
        state.pendingAccessToken = null;
        state.backendSignInError = null;
        state.isLoading = false;
        const authData = {
          user: userInfo,
          signinResponse: signInResponse,
        };
        storage.saveAuth(authData);
      })
      .addCase(fetchWhoami.fulfilled, (state, action) => {
        const whoamiData = action.payload;
        state.showAcknowledgement = !whoamiData.is_acknowledged;
        state.whoamiData = whoamiData; // Store whoami data for accessing personnel_record
      })
      .addCase(fetchWhoami.rejected, (state) => {
        state.showAcknowledgement = true;
      })
      .addCase(signInToBackend.rejected, (state, action) => {
        const error = action.payload;
        const errorCode =
          error?.error?.code ||
          error?.code ||
          error?.error?.context_code ||
          error?.context_code;

        if (
          errorCode === 'PERSONNEL_NOT_FOUND' ||
          errorCode === 'PERSONNEL_RECORD_NOT_FOUND'
        ) {
          state.isAuthenticated = false;
          state.user = null;
          state.sessionId = null;
          state.signinResponse = null;
          state.showPersonnelNotFound = true;
          state.backendSignInError = null;
        } else {
          state.backendSignInError = error.message || 'Backend signin failed';
          state.showPersonnelNotFound = false;
        }
        state.isLoading = false;
      })
      .addCase(checkEmailVerification.rejected, (state, action) => {
        const error = action.payload || {};
        if (error.isPersonnelError) {
          state.isAuthenticated = false;
          state.user = null;
          state.sessionId = null;
          state.signinResponse = null;
          state.showPersonnelNotFound = true;
          state.backendSignInError = null;
          state.isLoading = false;
          storage.removeAuth();
        }
      })
      .addCase(resendVerificationEmail.fulfilled, () => {})
      .addCase(resendVerificationEmail.rejected, () => {});
  },
});

export const {
  setLoading,
  setAuth0User,
  setAuth0Error,
  setBackendSignInError,
  setShowEmailVerification,
  setShowPersonnelNotFound,
  setShowAcknowledgement,
  setIsSigningOut,
  setPendingAccessToken,
  setAuthData,
  setUser,
  setSessionId,
  setSigninResponse,
  setIsAuthenticated,
  clearAuth,
  clearSigningOutState,
  setRef,
} = authSlice.actions;

export default authSlice.reducer;
